import { useState } from "react";
import { useTranslation } from "react-i18next";
import { lazyWithPreload } from "utils/lazyWithPreload";

import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { createWorkspaceDatabase } from "services/databases/createWorkspaceDatabase";
import { CreateEditDatabaseInput } from "services/databases/types";
import { ReactQueryKeysAccount } from "services/queryKeys";

import { StatusMessageType } from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";
import { queryClient } from "components/queryClient";

const DatabaseModal = lazyWithPreload(
  () => import("../DatabaseModal/DatabaseModal")
);

export const useCreateDatabase = () => {
  const [database, setCreateDatabase] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  const { putStatusMessage } = useStatusMessageQueue();
  usePreloadComponents([DatabaseModal]);

  const openCreateDatabase = () => {
    setCreateDatabase(true);
  };

  const closeCreateDatabase = () => {
    if (isLoading) {
      return;
    }
    setCreateDatabase(false);
  };

  const confirmCreate = async (database: CreateEditDatabaseInput) => {
    try {
      setIsLoading(true);
      await createWorkspaceDatabase(database);

      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.databasesNames],
      });
      closeCreateDatabase();
      putStatusMessage({
        message: t("workspace.create_database.success", {
          databaseName: database.catalogName,
        }),
        type: StatusMessageType.Success,
      });
    } catch (error: any) {
      console.log(error);
      putStatusMessage({
        message: error.message,
        type: StatusMessageType.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createDatabaseMarkup = database ? (
    <DatabaseModal
      title={t("workspace.create_database.title")}
      primaryButtonText="workspace.create_database.submit"
      onClose={closeCreateDatabase}
      onSubmit={confirmCreate}
      isLoading={isLoading}
    />
  ) : null;

  return {
    createDatabaseMarkup,
    openCreateDatabase,
  };
};
